import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  signOut as amplifySignOut,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  signInWithRedirect,
} from 'aws-amplify/auth';

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const AWS_ENDPOINT_URL = import.meta.env.VITE_AWS_ENDPOINT_URL || '';
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN || '';

const isLocalMode = !!AWS_ENDPOINT_URL;

// Configure Amplify for production mode.
// In local dev (moto), we bypass Amplify entirely and call moto's HTTP API
// directly, because Amplify does not support custom Cognito endpoints.
if (!isLocalMode) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: USER_POOL_ID,
        userPoolClientId: CLIENT_ID,
        loginWith: {
          ...(COGNITO_DOMAIN ? {
            oauth: {
              domain: COGNITO_DOMAIN.replace(/^https?:\/\//, ''),
              scopes: ['email', 'profile', 'openid'],
              redirectSignIn: [`${window.location.origin}/auth/callback`],
              redirectSignOut: [window.location.origin],
              responseType: 'code' as const,
            },
          } : {}),
        },
      },
    },
  });
}

// Local storage keys for mock auth (local mode only)
const STORAGE_KEYS = {
  tokens: 'waypoint_auth_tokens',
  user: 'waypoint_auth_user',
};

export interface SignUpParams {
  email: string;
  password: string;
  name: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

/**
 * Make a request to moto's Cognito API (local dev only)
 */
async function motoRequest(action: string, payload: Record<string, unknown>): Promise<unknown> {
  const response = await fetch(AWS_ENDPOINT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.__type || 'Cognito request failed');
  }

  return response.json();
}

/**
 * Returns true if the JWT is expired or will expire within the given buffer (ms).
 * Treats malformed tokens as expired. Used in local mode only.
 */
function isTokenExpired(token: string, bufferMs = 60_000): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + bufferMs;
  } catch {
    return true;
  }
}

/**
 * Sign up a new user with Cognito
 */
export const signUp = async (params: SignUpParams): Promise<{ cognitoSub: string }> => {
  if (isLocalMode) {
    const result = await motoRequest('SignUp', {
      ClientId: CLIENT_ID,
      Username: params.email,
      Password: params.password,
      UserAttributes: [
        { Name: 'email', Value: params.email },
        { Name: 'name', Value: params.name },
      ],
    }) as { UserSub: string };

    // Auto-confirm user in local mode
    await motoRequest('AdminConfirmSignUp', {
      UserPoolId: USER_POOL_ID,
      Username: params.email,
    });

    return { cognitoSub: result.UserSub };
  }

  const result = await amplifySignUp({
    username: params.email,
    password: params.password,
    options: {
      userAttributes: {
        email: params.email,
        name: params.name,
      },
    },
  });

  return { cognitoSub: result.userId || '' };
};

/**
 * Trigger a forgot-password reset code to be sent to the user's email
 */
export const forgotPassword = async (email: string): Promise<void> => {
  if (isLocalMode) {
    await motoRequest('ForgotPassword', {
      ClientId: CLIENT_ID,
      Username: email,
    });
    return;
  }

  await resetPassword({ username: email });
};

/**
 * Complete the forgot-password flow by submitting the code and new password
 */
export const confirmForgotPassword = async (email: string, code: string, newPassword: string): Promise<void> => {
  if (isLocalMode) {
    await motoRequest('ConfirmForgotPassword', {
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword,
    }) as Record<string, unknown>;
    return;
  }

  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
};

/**
 * Confirm a user's email address with the verification code sent by Cognito
 */
export const confirmSignUp = async (email: string, code: string): Promise<void> => {
  if (isLocalMode) {
    // Local mode users are auto-confirmed — nothing to do
    return;
  }

  await amplifyConfirmSignUp({ username: email, confirmationCode: code });
};

/**
 * Resend the email verification code
 */
export const resendConfirmationCode = async (email: string): Promise<void> => {
  if (isLocalMode) {
    return;
  }

  await resendSignUpCode({ username: email });
};

/**
 * Sign in user with email and password
 */
export const signIn = async (params: SignInParams): Promise<AuthTokens> => {
  if (isLocalMode) {
    const result = await motoRequest('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: CLIENT_ID,
      AuthParameters: {
        USERNAME: params.email,
        PASSWORD: params.password,
      },
    }) as {
      AuthenticationResult: {
        IdToken: string;
        AccessToken: string;
        RefreshToken: string;
      };
    };

    const tokens: AuthTokens = {
      idToken: result.AuthenticationResult.IdToken,
      accessToken: result.AuthenticationResult.AccessToken,
      refreshToken: result.AuthenticationResult.RefreshToken,
    };

    // Store tokens and user info locally
    localStorage.setItem(STORAGE_KEYS.tokens, JSON.stringify(tokens));
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify({ email: params.email }));

    return tokens;
  }

  await amplifySignIn({ username: params.email, password: params.password });

  // Fetch the session to return tokens in the expected shape
  const session = await fetchAuthSession();
  return {
    idToken: session.tokens?.idToken?.toString() ?? '',
    accessToken: session.tokens?.accessToken?.toString() ?? '',
    refreshToken: '',  // Amplify manages refresh tokens internally
  };
};

/**
 * Initiate Google sign-in via Cognito Hosted UI OAuth redirect.
 * Saves the intended destination to sessionStorage so the auth callback
 * page can navigate there after sign-in completes.
 */
export const signInWithGoogle = async (redirectAfter?: string, nickname?: string): Promise<void> => {
  if (isLocalMode) {
    throw new Error('Google sign-in is not available in local development mode');
  }

  if (redirectAfter) {
    sessionStorage.setItem('auth_redirect_after_login', redirectAfter);
  }
  if (nickname) {
    sessionStorage.setItem('pending_nickname', nickname);
  }

  await signInWithRedirect({ provider: 'Google' });
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  if (isLocalMode) {
    localStorage.removeItem(STORAGE_KEYS.tokens);
    localStorage.removeItem(STORAGE_KEYS.user);
    return;
  }

  await amplifySignOut();
};

/**
 * Get current user's ID token
 */
export const getCurrentUserToken = async (): Promise<string | null> => {
  if (isLocalMode) {
    const stored = localStorage.getItem(STORAGE_KEYS.tokens);
    if (!stored) return null;
    const tokens: AuthTokens = JSON.parse(stored);
    if (isTokenExpired(tokens.idToken)) {
      localStorage.removeItem(STORAGE_KEYS.tokens);
      localStorage.removeItem(STORAGE_KEYS.user);
      return null;
    }
    return tokens.idToken;
  }

  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
};

/**
 * Get current user's Cognito sub, email, and name from the session.
 */
export const getCurrentUserInfo = async (): Promise<{ sub: string; email: string; name: string } | null> => {
  if (isLocalMode) {
    const storedTokens = localStorage.getItem(STORAGE_KEYS.tokens);
    const storedUser = localStorage.getItem(STORAGE_KEYS.user);
    if (!storedTokens || !storedUser) return null;

    const tokens: AuthTokens = JSON.parse(storedTokens);
    if (isTokenExpired(tokens.idToken)) {
      localStorage.removeItem(STORAGE_KEYS.tokens);
      localStorage.removeItem(STORAGE_KEYS.user);
      return null;
    }

    const user = JSON.parse(storedUser);
    try {
      const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));
      return { sub: payload.sub, email: user.email, name: payload.name || '' };
    } catch {
      return null;
    }
  }

  try {
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload;
    if (!payload) return null;

    return {
      sub: payload.sub as string,
      email: payload.email as string,
      name: (payload.name as string) || '',
    };
  } catch {
    return null;
  }
};
